import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';
import { Eye, EyeOff, X, Check } from 'lucide-react';

export default function ActivatePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [step, setStep]         = useState('loading'); // loading | form | success | error
  const [userInfo, setUserInfo]  = useState(null);
  const [password, setPassword]  = useState('');
  const [confirm,  setConfirm]   = useState('');
  const [showPw,   setShowPw]    = useState(false);
  const [errors,   setErrors]    = useState({});
  const [loading,  setLoading]   = useState(false);

  // Validate the token on mount
  useEffect(() => {
    if (!token) {
      setStep('error');
      return;
    }
    apiService.validateActivationToken(token)
      .then(res => {
        const data = res?.data ?? res;
        setUserInfo(data);
        setStep('form');
      })
      .catch(() => setStep('error'));
  }, [token]);

  function validate() {
    const errs = {};
    if (password.length < 8)          errs.password = 'Password must be at least 8 characters';
    if (password !== confirm)          errs.confirm  = 'Passwords do not match';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await apiService.activateAccount({ token, password });
      setStep('success');
      // Auto-redirect to login after 2.5s
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      const msg = err?.response?.data?.message ?? 'Activation failed. The link may have expired.';
      setErrors({ _general: msg });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      {/* Logo — same hero lockup used on the Login page */}
      <img src="/assets/clockout-lockup-hero-2400.png" alt="Clock-out" className="auth-logo" />

      <div className="auth-card">

        {/* LOADING */}
        {step === 'loading' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              border: `3px solid rgba(28,26,20,0.15)`,
              borderTopColor: '#FFC81E',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ fontSize: 14, color: '#857a63', margin: 0 }}>Validating your link…</p>
          </div>
        )}

        {/* ERROR */}
        {step === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: '#fef2f2',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
              color: '#DC2626',
            }}>
              <X size={26} strokeWidth={2.2} aria-hidden="true" />
            </div>
            <h1 className="auth-title">
              Link expired or invalid
            </h1>
            <p style={{ fontSize: 14, color: '#857a63', lineHeight: 1.6, margin: '0 0 24px' }}>
              This activation link has expired or already been used.<br />
              Ask your admin to resend the invite.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="auth-btn-primary"
            >
              Go to sign in
            </button>
          </div>
        )}

        {/* FORM */}
        {step === 'form' && (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div>
              <h1 className="auth-title">
                Activate your account
              </h1>
              {userInfo?.firstName && (
                <p className="auth-subtitle">
                  Welcome, {userInfo.firstName}! Set a password to get started.
                </p>
              )}
            </div>

            {errors._general && (
              <div style={{
                padding: '10px 14px', borderRadius: 10,
                background: '#fef2f2', border: '1px solid #fecaca',
                fontSize: 13, color: '#c0392b',
              }}>
                {errors._general}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label className="auth-label" style={{ marginBottom: 0 }}>
                New password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPw ? 'text' : 'password'}
                  className="auth-input"
                  style={{
                    paddingRight: 44,
                    ...(errors.password ? { borderColor: '#fca5a5' } : null),
                  }}
                  placeholder="Minimum 8 characters"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setErrors(er => ({ ...er, password: '' })); }}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPw(s => !s)}
                  style={{
                    position: 'absolute', right: 12, top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#b5a889', fontSize: 13, padding: 0,
                  }}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && (
                <span style={{ fontSize: 11, color: '#c0392b' }}>{errors.password}</span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label className="auth-label" style={{ marginBottom: 0 }}>
                Confirm password
              </label>
              <input
                type={showPw ? 'text' : 'password'}
                className="auth-input"
                style={errors.confirm ? { borderColor: '#fca5a5' } : undefined}
                placeholder="Re-enter your password"
                value={confirm}
                onChange={e => { setConfirm(e.target.value); setErrors(er => ({ ...er, confirm: '' })); }}
              />
              {errors.confirm && (
                <span style={{ fontSize: 11, color: '#c0392b' }}>{errors.confirm}</span>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="auth-btn-primary"
              style={{ marginTop: 4 }}
            >
              {loading ? 'Activating…' : 'Activate account'}
            </button>
          </form>
        )}

        {/* SUCCESS */}
        {step === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'rgba(22,163,74,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
              color: '#16A34A',
            }}>
              <Check size={26} strokeWidth={2.2} aria-hidden="true" />
            </div>
            <h1 className="auth-title">
              Account activated!
            </h1>
            <p style={{ fontSize: 14, color: '#857a63', lineHeight: 1.6, margin: 0 }}>
              Redirecting you to login…
            </p>
          </div>
        )}

      </div>

      <p className="auth-footer">© {new Date().getFullYear()} Talk Nerdy To Me Consultancy Ltd. All rights reserved.</p>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}